import type { PickerState } from '@sg-bootstrap/components';
import { SgCategoryPicker } from '@sg-bootstrap/components/dist/react-library/src/components.js';
import { isDefined } from '@sgme/fp';
import { useEffect, useMemo, useRef } from 'react';

type CategoryPickerProps = {
  id?: string;
  placeholder?: string;
  item: string[] | undefined;
  items: readonly string[];
  handleChange: (itemsSelected: string[]) => void;
  itemsList?: {
    key: string;
    label: string;
  }[];
  state?: PickerState;
};

export const CategoryPicker = ({ id, placeholder, item, items, handleChange, itemsList, state }: CategoryPickerProps) => {
  const defaultItemsList = useMemo(
    () =>
      items.map((option) => ({
        key: option,
        label: option,
      })),
    [items],
  );

  const optionsList = isDefined(itemsList) ? itemsList : defaultItemsList;

  const pickerRef = useRef<HTMLSgCategoryPickerElement>(null);

  useEffect(() => {
    const picker = pickerRef.current;
    if (!picker) {
      return undefined;
    }

    return attachListeners(picker, handleChange);
  }, [handleChange]);

  useEffect(
    function updateOptionsList() {
      pickerRef.current?.setItems(
        optionsList.map((option) => ({
          ...option,
          selected: item?.includes(option.key),
        })),
      );
    },
    [optionsList, item],
  );

  return (
    <SgCategoryPicker
      data-e2e={`${id}-picker`}
      ref={pickerRef}
      noIcon
      classes="custom-category-picker"
      noClear
      size="sm"
      color="primary"
      inputMode="none"
      top
      noPropagation
      state={state}
      placeholder={isDefined(placeholder) ? placeholder : `Select ${isDefined(id) ? id : 'item'}`}
    />
  );
};

function stopPropagation(e: MouseEvent) {
  e.stopPropagation();
}

function closeOnClickingOutside(picker: HTMLSgCategoryPickerElement | null) {
  return () => {
    function clickOutside(e: MouseEvent): void {
      e.preventDefault();
      picker?.close();
      picker?.removeEventListener('click', stopPropagation);
    }
    document.addEventListener('click', clickOutside, { once: true });
    picker?.addEventListener('click', stopPropagation);
  };
}

function attachListeners(currentPicker: HTMLSgCategoryPickerElement, onChange: (itemsSelected: string[]) => void) {
  async function itemSelected() {
    const currentItems = (await currentPicker.getSelection()).map((item) => item.item.key);
    onChange((currentItems ?? []) as string[]);
  }

  const closeOnClick = closeOnClickingOutside(currentPicker);

  currentPicker.addEventListener('itemsSelected', itemSelected);
  currentPicker.addEventListener('itemsUnselected', itemSelected);
  currentPicker.addEventListener('clickOnPicker', closeOnClick);
  return () => {
    currentPicker.removeEventListener('clickOnPicker', closeOnClick);
    currentPicker.removeEventListener('itemsUnselected', itemSelected);
    currentPicker.removeEventListener('itemsSelected', itemSelected);
  };
}
